import type { EyeSample, FocusTelemetry } from "../types";

export interface TelemetryWindowConfig {
  /** Rolling window length, ms. */
  windowMs?: number;
  /** Gaze step magnitude above this between consecutive samples counts as a saccade. */
  saccadeThreshold?: number;
  /** How long (ms, from the first pushed sample) the blink-rate baseline EMA keeps updating before freezing. */
  baselineMs?: number;
}

export interface TelemetrySnapshotCtx {
  timeOnLevelS: number;
  level: number;
  distToWallPx: number;
  cursorSpeedPxS: number;
  now: number;
}

/** Wrap an angle delta (radians) into [-pi, pi], the shortest signed rotation. */
function wrapAngleDelta(d: number): number {
  const twoPi = 2 * Math.PI;
  let wrapped = ((d % twoPi) + twoPi) % twoPi;
  if (wrapped > Math.PI) wrapped -= twoPi;
  return wrapped;
}

function std(values: number[]): number {
  const n = values.length;
  if (n === 0) return 0;
  const mean = values.reduce((a, b) => a + b, 0) / n;
  const variance = values.reduce((a, b) => a + (b - mean) ** 2, 0) / n;
  return Math.sqrt(variance);
}

export class TelemetryWindow {
  private readonly windowMs: number;
  private readonly saccadeThreshold: number;
  private readonly baselineMs: number;

  private samples: EyeSample[] = [];
  private firstT: number | null = null;
  private baselineEma: number | null = null;
  private baselineFrozen = false;
  private lastBaselineT: number | null = null;

  constructor(cfg: TelemetryWindowConfig = {}) {
    this.windowMs = cfg.windowMs ?? 2000;
    this.saccadeThreshold = cfg.saccadeThreshold ?? 0.35;
    this.baselineMs = cfg.baselineMs ?? 20000;
  }

  push(sample: EyeSample): void {
    if (this.firstT === null) this.firstT = sample.t;

    this.samples.push(sample);
    this.trim(sample.t);
    this.updateBaseline(sample.t);
  }

  private trim(nowT: number): void {
    const cutoff = nowT - this.windowMs;
    while (this.samples.length > 0 && this.samples[0]!.t < cutoff) {
      this.samples.shift();
    }
  }

  private blinkEventCount(): number {
    let count = 0;
    let prevMean: number | null = null;
    for (const s of this.samples) {
      const mean = (s.blinkL + s.blinkR) / 2;
      if (prevMean !== null && prevMean <= 0.5 && mean > 0.5) count++;
      prevMean = mean;
    }
    return count;
  }

  private blinksPerMinInWindow(): number {
    return this.blinkEventCount() * (60000 / this.windowMs);
  }

  /**
   * Blink rate scaled by the span actually covered by samples so far, not the full
   * configured window — early on (fewer ms observed than `windowMs`) dividing by the
   * full window underestimates the rate. Floored at 250ms so a couple of samples right
   * at startup don't blow the rate up toward infinity.
   */
  private coveredBlinksPerMin(nowT: number): number {
    const oldest = this.samples[0];
    const span = oldest === undefined ? this.windowMs : nowT - oldest.t;
    const covered = Math.max(250, Math.min(this.windowMs, span));
    return this.blinkEventCount() * (60000 / covered);
  }

  private updateBaseline(nowT: number): void {
    if (this.baselineFrozen) return;
    const currentRate = this.coveredBlinksPerMin(nowT);
    if (this.baselineEma === null) {
      this.baselineEma = currentRate;
    } else {
      // Time-based EMA (not per-push) so 30fps and 60fps converge at the same wall-clock
      // rate instead of the update cadence itself changing how fast the baseline moves.
      const dtS = Math.max(0, this.lastBaselineT === null ? 0 : nowT - this.lastBaselineT) / 1000;
      const tauS = 5;
      const alpha = 1 - Math.exp(-dtS / tauS);
      this.baselineEma = alpha * currentRate + (1 - alpha) * this.baselineEma;
    }
    this.lastBaselineT = nowT;
    if (this.firstT !== null && nowT - this.firstT >= this.baselineMs) {
      this.baselineFrozen = true;
    }
  }

  private saccadeCount(): number {
    let count = 0;
    for (let i = 1; i < this.samples.length; i++) {
      const prev = this.samples[i - 1]!;
      const cur = this.samples[i]!;
      const dx = cur.gaze.x - prev.gaze.x;
      const dy = cur.gaze.y - prev.gaze.y;
      if (Math.hypot(dx, dy) > this.saccadeThreshold) count++;
    }
    return count;
  }

  private headMotionDegPerS(): number {
    if (this.samples.length < 2) return 0;
    let total = 0;
    let count = 0;
    for (let i = 1; i < this.samples.length; i++) {
      const prev = this.samples[i - 1]!;
      const cur = this.samples[i]!;
      const dtS = (cur.t - prev.t) / 1000;
      if (dtS <= 0) continue;
      // Wrap each axis delta into [-pi, pi] before combining: a raw subtraction of two
      // wrapped Euler angles (e.g. +pi-eps -> -pi+eps) reads as a ~2pi swing instead of
      // the small actual motion.
      const dyaw = wrapAngleDelta(cur.headRot.yaw - prev.headRot.yaw);
      const dpitch = wrapAngleDelta(cur.headRot.pitch - prev.headRot.pitch);
      const droll = wrapAngleDelta(cur.headRot.roll - prev.headRot.roll);
      const magRad = Math.hypot(dyaw, dpitch, droll);
      total += (magRad * (180 / Math.PI)) / dtS;
      count++;
    }
    return count === 0 ? 0 : total / count;
  }

  snapshot(ctx: TelemetrySnapshotCtx): FocusTelemetry {
    this.trim(ctx.now);

    const gazeX = this.samples.map((s) => s.gaze.x);
    const gazeY = this.samples.map((s) => s.gaze.y);
    const faceCount = this.samples.filter((s) => s.facePresent).length;

    return {
      windowMs: this.windowMs,
      samples: this.samples.length,
      gazeStdX: std(gazeX),
      gazeStdY: std(gazeY),
      saccades: this.saccadeCount(),
      blinksPerMin: this.blinksPerMinInWindow(),
      baselineBlinksPerMin: this.baselineEma ?? 0,
      headMotionDegPerS: this.headMotionDegPerS(),
      facePresentRatio: this.samples.length === 0 ? 0 : faceCount / this.samples.length,
      timeOnLevelS: ctx.timeOnLevelS,
      level: ctx.level,
      distToWallPx: ctx.distToWallPx,
      cursorSpeedPxS: ctx.cursorSpeedPxS,
    };
  }
}
